import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { useForm } from 'react-hook-form';

import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as accountingSlice from '../../../data/accounting.slice';
import * as referenceSlice from '../../../data/reference.slice';
import {
  PricingModel,
  PricingModelCharge,
} from '../../../data/models/pricing-model.model';
import { ProviderServicePricing } from '../../../data/models/provider-service-pricing.model';
import { PricingFunction } from '../../../data/models/pricing-function.model';
import ChipSelectMenu from '../../form/ChipSelectMenu';
import TextInput from '../../form/TextInput';
import SelectMenu from '../../form/SelectMenu';
import { BaseProvider, Provider } from '../../../data/models/provider.model';
import ChargeTypeItem from './ChargeTypeItem';

const PREFIX = 'ProviderServicePricingDialog';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  left: `${PREFIX}-left`,
  right: `${PREFIX}-right`,
  pricing: `${PREFIX}-pricing`,
  chargeTypeGridCell: `${PREFIX}-chargeTypeGridCell`,
  dates: `${PREFIX}-dates`,
  date: `${PREFIX}-date`,
  footer: `${PREFIX}-footer`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.container}`]: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    justtifyContent: 'space-evenly',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    margin: theme.spacing(2),
  },
  [`& .${classes.right}`]: {
    flex: '1 1 0px',
    minWidth: 600,
    width: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.pricing}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
  },
  [`& .${classes.dates}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  [`& .${classes.date}`]: {
    flexGrow: 1,
  },
  [`& .${classes.footer}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  [`& .${classes.chargeTypeGridCell}`]: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const defaultProviderPricing = {
  name: '',
  description: '',
  coverageAreaIDs: [] as number[],
  transportationServiceUUIDs: [] as string[],
  pricingModel: undefined as string | undefined,
  params: [] as any[],
} as any;

interface ProviderServicePricingDialogProps {
  provider: Provider | BaseProvider;
  providerServicePricing?: ProviderServicePricing;
  pricingModels: PricingModel[];
  open: boolean;
  close: () => void;
  title: string;
}

export default function ProviderServicePricingDialog({
  provider,
  providerServicePricing,
  pricingModels,
  open,
  close,
  title,
}: ProviderServicePricingDialogProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const saving = useAppSelector(
    accountingSlice.selectSavingProviderServicePricing,
  );
  const saved = useAppSelector(
    accountingSlice.selectSavedProviderServicePricing,
  );

  const pricingFunctions = useAppSelector(
    accountingSlice.selectAllPricingFunctions,
  );
  const chargeTypes = useAppSelector(referenceSlice.selectChargeTypes);
  const [pricingModel, setPricingModel] = React.useState<PricingModel>();

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: defaultProviderPricing,
  });

  const selectedPricingModel = formHook.watch('pricingModel');
  const selectedTransportationServices = formHook.watch(
    'transportationServiceUUIDs',
  );

  useEffect(() => {
    dispatch(referenceSlice.fetchChargeTypesAsync());
  }, [dispatch]);

  useEffect(() => {
    if (providerServicePricing) {
      const catalogPricingModel = pricingModels.find(
        (p) =>
          p.pricingModelUUID ===
          providerServicePricing.pricingModel.pricingModelUUID,
      );
      const params = (catalogPricingModel?.chargeTypes || []).map((ct) => {
        const charge = providerServicePricing.pricingModel.charges.find(
          (c) => c.chargeTypeBC === ct.chargeTypeBC,
        );
        const fees: any = {};
        charge?.functionParameters.forEach((fp) => {
          fees[fp.name] = fp.value;
        });
        return fees;
      });
      formHook.reset({
        name: providerServicePricing.name,
        description: providerServicePricing.description,
        coverageAreaIDs: providerServicePricing.coverageAreaIDs,
        transportationProviderID:
          providerServicePricing.transportationProviderID,
        transportationServiceUUIDs:
          providerServicePricing.transportationServiceUUIDs,
        pricingModel: providerServicePricing.pricingModel.pricingModelUUID,
        params,
      });
    } else {
      formHook.reset(defaultProviderPricing);
    }
  }, [providerServicePricing, pricingModels]);

  useEffect(() => {
    if (selectedPricingModel) {
      const pm = pricingModels.find(
        (p) => p.pricingModelUUID === selectedPricingModel,
      );
      setPricingModel(pm);
    } else {
      setPricingModel(undefined);
    }
  }, [selectedPricingModel]);

  const transportServices = useAppSelector(
    accountingSlice.selectAllTransportationServices,
  );

  const getPricingFunction = (
    pricingFunctionBC: string,
  ): PricingFunction | undefined => {
    return pricingFunctions.find(
      (c) => c.pricingFunctionBC === pricingFunctionBC,
    );
  };

  const createProviderServicePricing = (data: any): ProviderServicePricing => {
    const charges: PricingModelCharge[] = pricingModel!.chargeTypes.map(
      (ct, idx): PricingModelCharge => {
        const pricingFunction = getPricingFunction(ct.pricingFunctionBC);
        const params =
          pricingFunction?.parameters.filter(
            (p) => p.type === 'UserSupplied',
          ) || [];
        const functionParameters = params.map((p) => {
          return {
            name: p.name,
            value: `${data.params[idx][p.name]}`,
          };
        });
        return {
          chargeTypeBC: ct.chargeTypeBC,
          pricingFunctionBC: pricingFunction!.pricingFunctionBC,
          functionParameters,
        };
      },
    );

    const payload: any = {
      name: data.name,
      description: data.description,
      transportationProviderID: provider.transportationProviderID,
      coverageAreaIDs: data.coverageAreaIDs,
      transportationServiceUUIDs: data.transportationServiceUUIDs,
      pricingModel: {
        pricingModelUUID: selectedPricingModel,
        charges,
      },
    };

    if (providerServicePricing) {
      payload.providerServicePricingUUID =
        providerServicePricing.providerServicePricingUUID;
    }

    return payload;
  };

  const save = (data: any) => {
    const newProviderServicePricing = createProviderServicePricing(data);
    if (providerServicePricing) {
      dispatch(
        accountingSlice.updateProviderServicePricingAsync(
          newProviderServicePricing,
        ),
      );
    } else {
      dispatch(
        accountingSlice.createProviderServicePricingAsync(
          newProviderServicePricing,
        ),
      );
    }
  };

  useEffect(() => {
    if (!saving && saved) {
      formHook.reset();
      close();
      dispatch(accountingSlice.setSavedProviderServicePricing(false));
    }
  }, [saving, saved, close]);

  const cancel = () => {
    formHook.reset();
    close();
  };

  return (
    <RootDialog maxWidth="lg" open={open} onClose={cancel}>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          {title}
        </Typography>
        <IconButton
          onClick={cancel}
          aria-label="cancel"
          data-testid="cancel"
          size="large">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent className={classes.container}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Box className={classes.right}>
            <TextInput
              name="name"
              label={t('common.name')}
              control={formHook.control}
              required={true}
              rules={{ required: requiredFieldMessage }}
            />
            <TextInput
              name="description"
              label={t('common.description')}
              control={formHook.control}
              multiline
            />
            <ChipSelectMenu
              label={t('provider.coverageAreas')}
              name="coverageAreaIDs"
              values={provider.coverageAreas || []}
              valueAttr="coverageAreaID"
              labelAttr="coverageAreaName"
              required={true}
              control={formHook.control}
              rules={{ required: requiredFieldMessage }}
              showOkCancel
            />
            <ChipSelectMenu
              label={t('provider.transportationServices')}
              name="transportationServiceUUIDs"
              values={transportServices}
              valueAttr="transportationServiceUUID"
              labelAttr="name"
              required={true}
              control={formHook.control}
              rules={{ required: requiredFieldMessage }}
              showOkCancel
            />
            <SelectMenu
              label={t('provider.pricingModel')}
              name="pricingModel"
              values={pricingModels}
              valueAttr="pricingModelUUID"
              labelAttr="name"
              required={true}
              control={formHook.control}
              rules={{ required: requiredFieldMessage }}
            />

            {selectedTransportationServices?.length > 0 &&
              !!selectedPricingModel && (
                <Paper className={classes.pricing}>
                  <Grid container spacing={2}>
                    {pricingModel?.chargeTypes.map((ct, idx) => (
                      <ChargeTypeItem
                        key={idx}
                        chargeType={ct}
                        formHook={formHook}
                        idx={idx}
                      />
                    ))}
                  </Grid>
                </Paper>
              )}
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <Box className={classes.footer}>
        <Box className={classes.actions}>
          <Button variant="contained" color="secondary" onClick={cancel}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={
              !formHook.formState.isValid || !formHook.formState.isDirty
            }
            onClick={formHook.handleSubmit(save)}>
            {t('common.save')}
          </Button>
        </Box>
      </Box>
    </RootDialog>
  );
}
